import type { TFunction } from '@unifyapps/i18n/client';
import type { TypographyTokenGroup } from './types';
import { TypographyHierarchyType } from './types';

export const BASE_FONT_TOKEN = '--fontSize-text-sm';

export const getTypographyConfig = (t: TFunction) => ({
  title: t('theme:TypographyConfig.BaseFontSize'),
  description: t('theme:TypographyConfig.BaseFontSizeDescription'),
  id: TypographyHierarchyType.Base,
  token: '--fontSize-text-sm',
  autoGeneratedTokens: [
    {
      token: '--fontSize-display-2xl',
      title: t('theme:TypographyConfig.Heading1'),
    },
    {
      token: '--fontSize-display-xl',
      title: t('theme:TypographyConfig.Heading2'),
    },
    {
      token: '--fontSize-display-lg',
      title: t('theme:TypographyConfig.Heading3'),
    },
    {
      token: '--fontSize-display-md',
      title: t('theme:TypographyConfig.Heading4'),
    },
    {
      token: '--fontSize-display-sm',
      title: t('theme:TypographyConfig.SubTitle1'),
    },
    {
      token: '--fontSize-display-xs',
      title: t('theme:TypographyConfig.SubTitle2'),
    },
    {
      token: '--fontSize-text-xl',
      title: t('theme:TypographyConfig.Body1'),
    },
    {
      token: '--fontSize-text-lg',
      title: t('theme:TypographyConfig.Body2'),
    },
    {
      token: '--fontSize-text-md',
      title: t('theme:TypographyConfig.Body3'),
    },
    {
      token: '--fontSize-text-sm',
      title: t('theme:TypographyConfig.Body4'),
    },
    {
      token: '--fontSize-text-xsm',
      title: t('theme:TypographyConfig.Body5'),
    },
    {
      token: '--fontSize-text-xs',
      title: t('theme:TypographyConfig.Body6'),
    },
    {
      token: '--fontSize-text-xxs',
      title: t('theme:TypographyConfig.Body7'),
    },
    {
      token: '--fontSize-text-xxxs',
      title: t('theme:TypographyConfig.Body8'),
    },
    {
      token: '--fontSize-para-md',
      title: t('theme:TypographyConfig.Para1'),
    },
    {
      token: '--fontSize-para-sm',
      title: t('theme:TypographyConfig.Para2'),
    },
    {
      token: '--fontSize-para-xs',
      title: t('theme:TypographyConfig.Para3'),
    },
    {
      token: '--fontSize-text-code-xs',
      title: t('theme:TypographyConfig.Code1'),
    },
    {
      token: '--fontSize-text-code-xxs',
      title: t('theme:TypographyConfig.Code2'),
    },
    {
      token: '--fontSize-text-code-xxxs',
      title: t('theme:TypographyConfig.Code3'),
    },
  ],
  autoGeneratedTokenGroups: [
    {
      id: 'headings',
      title: t('theme:TypographyConfig.Headings'),
      description: t('theme:TypographyConfig.HeadingsDescription'),
      previewType: 'heading' as const,
      tokens: [
        {
          token: '--fontSize-display-2xl',
          title: t('theme:TypographyConfig.Heading1'),
          shortTitle: t('theme:TypographyConfig.H1'),
        },
        {
          token: '--fontSize-display-xl',
          title: t('theme:TypographyConfig.Heading2'),
          shortTitle: t('theme:TypographyConfig.H2'),
        },
        {
          token: '--fontSize-display-lg',
          title: t('theme:TypographyConfig.Heading3'),
          shortTitle: t('theme:TypographyConfig.H3'),
        },
        {
          token: '--fontSize-display-md',
          title: t('theme:TypographyConfig.Heading4'),
          shortTitle: t('theme:TypographyConfig.H4'),
        },
      ],
    },
    {
      id: 'subtitles',
      title: t('theme:TypographyConfig.Subtitles'),
      description: t('theme:TypographyConfig.SubtitlesDescription'),
      previewType: 'heading' as const,
      tokens: [
        {
          token: '--fontSize-display-sm',
          title: t('theme:TypographyConfig.SubTitle1'),
          shortTitle: t('theme:TypographyConfig.Sub1'),
        },
        {
          token: '--fontSize-display-xs',
          title: t('theme:TypographyConfig.SubTitle2'),
          shortTitle: t('theme:TypographyConfig.Sub2'),
        },
      ],
    },
    {
      id: 'body',
      title: t('theme:TypographyConfig.BodyGroup'),
      description: t('theme:TypographyConfig.BodyGroupDescription'),
      previewType: 'body' as const,
      tokens: [
        { token: '--fontSize-text-xl', title: t('theme:TypographyConfig.Body1') },
        { token: '--fontSize-text-lg', title: t('theme:TypographyConfig.Body2') },
        { token: '--fontSize-text-md', title: t('theme:TypographyConfig.Body3') },
        { token: '--fontSize-text-sm', title: t('theme:TypographyConfig.Body4') },
        { token: '--fontSize-text-xsm', title: t('theme:TypographyConfig.Body5') },
        { token: '--fontSize-text-xs', title: t('theme:TypographyConfig.Body6') },
        { token: '--fontSize-text-xxs', title: t('theme:TypographyConfig.Body7') },
        { token: '--fontSize-text-xxxs', title: t('theme:TypographyConfig.Body8') },
      ],
    },
    {
      id: 'paragraph',
      title: t('theme:TypographyConfig.ParagraphGroup'),
      description: t('theme:TypographyConfig.ParagraphGroupDescription'),
      previewType: 'body' as const,
      tokens: [
        { token: '--fontSize-para-md', title: t('theme:TypographyConfig.Para1') },
        { token: '--fontSize-para-sm', title: t('theme:TypographyConfig.Para2') },
        { token: '--fontSize-para-xs', title: t('theme:TypographyConfig.Para3') },
      ],
    },
    {
      id: 'code',
      title: t('theme:TypographyConfig.CodeGroup'),
      description: t('theme:TypographyConfig.CodeGroupDescription'),
      previewType: 'code' as const,
      tokens: [
        { token: '--fontSize-text-code-xs', title: t('theme:TypographyConfig.Code1') },
        { token: '--fontSize-text-code-xxs', title: t('theme:TypographyConfig.Code2') },
        { token: '--fontSize-text-code-xxxs', title: t('theme:TypographyConfig.Code3') },
      ],
    },
  ] satisfies TypographyTokenGroup[],
});

export const TEXT_FONT_FAMILY_TOKEN = ['--fontFamily-text', '--fontFamily-display'];
export const CODE_FONT_FAMILY_TOKEN = ['--fontFamily-code'];
